import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Form, Row, Col } from "react-bootstrap";
import useMediaQuery from "../../hooks/useMediaQuery";
import CustomCalendar from "../../components/CustomCalendar/CustomCalendar";
import moment from "moment";
import 'moment/locale/it';
import CustomModal from "../../components/CustomModal/CustomModal";
import ComebackArrow from "../../components/ComebackArrow/ComebackArrow";
import { alertService } from '../../services/AlertService';
import useEsami from "../../hooks/useEsami";
import useCorsiUtenti from "../../hooks/useCorsiUtenti";
import useEsamiUtenti from "../../hooks/useEsamiUtenti";
import EsamiUtentiService from '../../services/EsamiUtentiService/EsamiUtentiService';
import CustomButton from "../../components/CustomButton/CustomButton";
import useClassiUtenti from "../../hooks/useClassiUtenti";
import { TIPO_CORSO } from "../../model/tipoCorso";

const MODAL_TYPES = {
  NEW: 'NEW',
  EDIT: 'EDIT',
  VIEW: 'VIEW',
  DELETE: 'DELETE',
  MANAGE_USERS: 'MANAGE_USERS'
};

export default function EsamiConfigPage() {
  const {
    getEsamiByIdClasse,
    createEsame,
    deleteEsame,
    updateEsame,
    loadingStateEsami,
  } = useEsami();

  const {
    updateEsameUtente,
    loadingStateEsamiUtenti,
    enterJoinroomAdmin
  } = useEsamiUtenti();

const { getClassiUtentiByIdClasse } = useClassiUtenti();

  const location = useLocation();
  const navigate = useNavigate();
  const classe = location.state?.classeData;
  const isWideScreen = useMediaQuery("(min-width: 1200px)");
  const [exams, setExams] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newExam, setNewExam] = useState({
    data: "",
    ora_inizio: "",
    ora_fine: "",
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingExamId, setEditingExamId] = useState(null);
  const [modalMode, setModalMode] = useState(MODAL_TYPES.NEW);

  const [corsoUtenti, setCorsoUtenti] = useState([]);
  const [selectedExamUsers, setSelectedExamUsers] = useState([]);
  const userPrenotato = newExam?.utenti?.find(u => u.prenotato);

  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [examCountdowns, setExamCountdowns] = useState({});

  useEffect(() => {
    if(!classe?.id_classe) {
      if (classe?.corso?.modalita_erogazione === TIPO_CORSO.TELEMATICA_ASINCRONA) {
        navigate("/gestione-corsi")
      } else {
        navigate("/gestione-classi")
      }
      return
    }
    fetchExams();
    if(classe?.corso?.modalita_erogazione !== TIPO_CORSO.TELEMATICA_ASINCRONA) {
      fetchCorsoUtenti();
    }
  }, [classe]);

  const fetchExams = async () => {
    if (classe?.id_classe) {
      const obj = { id_classe: classe.id_classe }
      const fetchedExams = await getEsamiByIdClasse(obj);
      if (Array.isArray(fetchedExams)) {
        const formattedExams = fetchedExams.map(exam => ({
          id_calendario_esame: exam.id_calendario_esame,
          data: exam?.data?.split(' ')[0],
          ora_inizio: exam?.ora_inizio,
          ora_fine: exam?.ora_fine,
          utenti: exam?.utenti || []
        }));
        setExams(formattedExams);
      } else {
        setExams([]);
      }
    }
  };

  const fetchCorsoUtenti = async () => {
    if (classe?.corso?.id_corso) {
      const fetchedCorsoUtenti = await getClassiUtentiByIdClasse({ id_classe: classe.id_classe });
      const corsisti = fetchedCorsoUtenti.filter(utente => utente.tipo_utente === 'corsista');
      setCorsoUtenti(corsisti);
    }
  };

  const minDate = classe?.data_fine ? moment(classe.data_fine, "YYYY-MM-DD HH:mm:ss").toDate() : new Date();

  useEffect(() => {
    moment.locale('it'); 
  }, []);

  const formatDate = (date) => {
    if (!date) return '';
    const momentDate = moment(date);
    return momentDate.format('dddd D MMMM YYYY').replace(/^\w/, (c) => c.toUpperCase());
  };

  const handleSelectSlot = (slotInfo) => {
    const data = moment(slotInfo.start).format("YYYY-MM-DD");
    const ora_inizio = moment(slotInfo.start).format("HH:mm");
    const ora_fine = moment(slotInfo.end).format("HH:mm");
    setNewExam({ data, ora_inizio, ora_fine });
    setIsEditMode(false);
    setEditingExamId(null);
    setModalMode(MODAL_TYPES.NEW);
    setShowModal(true);
  };

  const handleSelectEvent = (event) => {
    const exam = exams.find(e => e.id_calendario_esame === event.id_calendario_esame);
    if (exam) {
      setNewExam({
        ...exam,
        utenti: exam.utenti || []
      });
      setIsEditMode(true);
      setEditingExamId(exam.id_calendario_esame);
      setModalMode(MODAL_TYPES.VIEW);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewExam({ data: "", ora_inizio: "", ora_fine: "" });
    setIsEditMode(false);
    setEditingExamId(null);
    setModalMode(MODAL_TYPES.NEW);
  };

  const handleSaveExam = async () => {
    if (newExam.ora_fine <= newExam.ora_inizio) {
      alertService.warn("L'ora di fine deve essere successiva all'ora di inizio", {autoClose: true});
      return;
    }

    const examData = {
      id_corso: classe?.corso.id_corso,
      id_classe: classe?.id_classe,
      data: newExam?.data,
      ora_inizio: newExam?.ora_inizio,
      ora_fine: newExam?.ora_fine,
    };

    if (isEditMode) {
      examData.id_calendario_esame = editingExamId;
      const updatedExam = await updateEsame(examData);
      if(updatedExam === -1){
        alertService.warn("Esiste già un esame previsto all'interno di questa fascia oraria.", {autoClose: true});
      }
      else{
        setExams(exams.map(exam => 
          exam.id_calendario_esame === editingExamId ? {
            ...exam,
            data: updatedExam?.data?.split(' ')[0],
            ora_inizio: updatedExam?.ora_inizio,
            ora_fine: updatedExam?.ora_fine
          } : exam
        ));
      }
    } else {
      const createdExam = await createEsame(examData);
      if(createdExam === -1){
        alertService.warn("Esiste già un esame previsto all'interno di questa fascia oraria.", {autoClose: true});
      }
      else{
        setExams([...exams, {
          id_calendario_esame: createdExam.id_calendario_esame,
          data: createdExam?.data?.split(' ')[0],
          ora_inizio: createdExam?.ora_inizio,
          ora_fine: createdExam?.ora_fine
        }]);
      }
    }
    handleCloseModal();
  };

  const handleEditClick = () => {
    setModalMode(MODAL_TYPES.EDIT);
  };

  const handleDeleteClick = () => {
    setModalMode(MODAL_TYPES.DELETE);
  };

  const handleDeleteExam = async () => {
    await deleteEsame({ id_esame: editingExamId });
    setExams(exams.filter(exam => exam.id_calendario_esame !== editingExamId));
    handleCloseModal();
  };

  const handleCancelDelete = () => {
    setModalMode(MODAL_TYPES.VIEW);
  };

  const handleManageUsers = async (exam) => {
    setNewExam(exam);
    setSelectedExamUsers(exam.utenti || []);
    setModalMode(MODAL_TYPES.MANAGE_USERS);
  };

  const handleUserSelection = (user) => {
    setSelectedExamUsers(prev => 
      prev.some(u => u.id_utente === user.id_utente)
        ? prev.filter(u => u.id_utente !== user.id_utente)
        : [...prev, user]
    );
  };

  const handleSaveUserSelection = async () => {
      const obj = {
        id_calendario_esame: newExam.id_calendario_esame,
        utenti: selectedExamUsers
      }
      await updateEsameUtente(obj);
      handleCloseModal();
      fetchExams();
  };

  const handleConfirmCancelUserBooking = async () => {
    if (!userPrenotato) return;
    try {
      await EsamiUtentiService.deleteEsameUtente({
        id_esame_utente: userPrenotato.id_calendario_esame_utente
      });
      alertService.success("Prenotazione annullata con successo.", { autoClose: true });
      setShowConfirmDeleteModal(false);
      handleCloseModal();
      fetchExams();
    } catch (error) {
      alertService.error('Errore durante l\'annullamento della prenotazione.');
    }
  };

  useEffect(() => {
    updateCountdowns(); // chiamata iniziale
    const interval = setInterval(updateCountdowns, 1000);
    return () => clearInterval(interval);
  }, [exams]);
  
  const updateCountdowns = () => {
    const now = moment();
    const updatedCountdowns = {};
  
    exams.forEach(exam => {
      const start = moment(`${exam.data}T${exam.ora_inizio}`);
      const diffSeconds = start.diff(now, 'seconds');
  
      if (diffSeconds <= 0) {
        updatedCountdowns[exam.id_calendario_esame] = 'INIZIATO';
      } else if (diffSeconds <= 86400) {
        if (diffSeconds < 60) {
          updatedCountdowns[exam.id_calendario_esame] = `Inizio tra ${diffSeconds} second${diffSeconds === 1 ? 'o' : 'i'}`;
        } else if (diffSeconds < 3600) {
          const minutes = Math.floor(diffSeconds / 60);
          updatedCountdowns[exam.id_calendario_esame] = `Inizio tra ${minutes} minut${minutes === 1 ? 'o' : 'i'}`;
        } else {
          const duration = moment.duration(start.diff(now));
          const hours = duration.hours();
          const minutes = duration.minutes();
          updatedCountdowns[exam.id_calendario_esame] = `Inizio tra ${hours} or${hours === 1 ? 'a' : 'e'} e ${minutes} minut${minutes === 1 ? 'o' : 'i'}`;
        }
      }
    });
  
    setExamCountdowns(prev => {
      const changed = Object.keys(updatedCountdowns).some(
        key => updatedCountdowns[key] !== prev[key]
      );
      return changed ? updatedCountdowns : prev;
    });
  };

  const enterJoinRoom = async (id_calendario_esame, exam) => {
      try {
        let response;
        var id_utente = exam.utenti[0].id_utente;//FISSO IN CASO DI UN UTENTE PER SINGOLA DATA ESAME
        console.log(exam.utenti[0].id_utente);
        response = await enterJoinroomAdmin({ id_calendario_esame: id_calendario_esame, id_user: id_utente });
        var meeting_id = response[0].meeting_id;
        var enc_str = response[0].enc_str;
        if(meeting_id.length > 0){
            var redirect = 'https://www.jampink.com/jp-meeting/'+ meeting_id+'?se='+enc_str;
            window.location = redirect;
        }
      } catch (error) {
        alertService.error('Errore durante l\'accesso alla Joinroom di esame.');
      }
    };
  
  
 /*  const handleUploadStatino = async (event, idCalendarioEsameUtente) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('statino', file);
    formData.append('id_calendario_esame_utente', 63);

    try {
      await EsamiUtentiService.uploadStatino(formData);
      alertService.success('Statino caricato con successo');
    } catch (error) {
      alertService.error('Errore nel caricamento dello statino: ' + error.message);
    }
  }; */

  const renderModalBody = () => {
    switch (modalMode) {
      case MODAL_TYPES.NEW:
      case MODAL_TYPES.EDIT:
        return (
          <Form>
            <Row>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label><span className='text-muted'><b>Orario inizio</b></span></Form.Label>
                  <Form.Control
                    type="time"
                    value={newExam?.ora_inizio || ''}
                    onChange={(e) => setNewExam({ ...newExam, ora_inizio: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label><span className='text-muted'><b>Orario fine</b></span></Form.Label>
                  <Form.Control
                    type="time"
                    value={newExam?.ora_fine || ''}
                    onChange={(e) => setNewExam({ ...newExam, ora_fine: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        );
      case MODAL_TYPES.VIEW:
        const corsistiAbilitati = newExam.utenti || [];
        const corsistiPrenotati = corsistiAbilitati?.filter(u => u.prenotato);
        return (
          <div>
            <div className="mb-4">
              <p><strong>Ora inizio:</strong> {newExam?.ora_inizio?.split(':').slice(0, 2).join(':')}</p>
              <p><strong>Ora fine:</strong> {newExam?.ora_fine?.split(':').slice(0, 2).join(':')}</p>

              {classe?.corso?.modalita_erogazione !== TIPO_CORSO.TELEMATICA_ASINCRONA && (
                <>
                  <h6>Corsisti abilitati alla prenotazione: {corsistiAbilitati?.length}</h6>
                  <h6 className="mt-3">Corsisti prenotati: {corsistiPrenotati?.length}</h6>
                </>
              )}
            </div>
            
            <div className="row g-4">
              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-body d-flex flex-column align-items-center justify-content-center">
                    <i className="bi bi-people-fill fs-1 mb-2 text-primary"></i>
                    <h5 className="card-title text-center">Gestisci Corsisti</h5>
                    <Button 
                      variant="outline-primary" 
                      onClick={() => handleManageUsers(newExam)} 
                      className="mt-2"
                    >
                      <i className="bi bi-pencil-square me-2"></i>
                      Gestisci
                    </Button>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-body d-flex flex-column align-items-center justify-content-center">
                    <i className="bi bi-clipboard-check fs-1 mb-2 text-success"></i>
                    <h5 className="card-title text-center">Risultati Esame</h5>
                    <Button 
                      variant="outline-success" 
                      onClick={() => navigate("/gestione-risultati-esame", { state: { examData: newExam, classeData: classe } })} 
                      className="mt-2"
                    >
                      <i className="bi bi-arrow-right-circle me-2"></i>
                      Gestisci
                    </Button>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-body d-flex flex-column align-items-center justify-content-center">
                    <i className="bi bi-clock-history fs-1 mb-2 text-info"></i>
                    <h5 className="card-title text-center">Modifica Orario</h5>
                    <Button 
                      variant="outline-info" 
                      onClick={handleEditClick} 
                      className="mt-2"
                    >
                      <i className="bi bi-pencil me-2"></i>
                      Modifica
                    </Button>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-body d-flex flex-column align-items-center justify-content-center">
                    <i className="bi bi-trash fs-1 mb-2 text-danger"></i>
                    <h5 className="card-title text-center">Elimina Esame</h5>
                    <Button 
                      variant="outline-danger" 
                      onClick={handleDeleteClick} 
                      className="mt-2"
                    >
                      <i className="bi bi-x-circle me-2"></i>
                      Elimina
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case MODAL_TYPES.DELETE:
        return (
          <div>
            <p>Sei sicuro di voler eliminare questo esame?</p>
            <CustomButton 
              label="Conferma Eliminazione"
              variant="danger"
              isLoading={loadingStateEsami.isLoadingEsameDelete}
              onClick={handleDeleteExam}
              className="mx-1"
            />
            <CustomButton 
              label="Indietro"
              variant="secondary"
              onClick={handleCancelDelete}
              className="mx-1"
            />
          </div>
        );
      case MODAL_TYPES.MANAGE_USERS:
        const userPrenotato = newExam.utenti?.find(u => u.prenotato);
        return (
          <>
            {
              classe?.corso?.modalita_erogazione === TIPO_CORSO.TELEMATICA_ASINCRONA ? (
                <div>
                  {userPrenotato ? (
                    <div className="d-flex flex-column align-items-start">
                      <h5>Corsista prenotato: {userPrenotato.nome} {userPrenotato.cognome} ({userPrenotato.luogo_nascita}, {moment(userPrenotato.data_nascita).format('DD/MM/YYYY')})</h5>
                    </div>
                  ) : (
                    <h5>Nessun corsista prenotato</h5>
                  )}
                </div>
              ) : (
                <div>
                  <h5>Abilita corsisti alla prenotazione per l'esame del {moment(newExam.data).format('DD/MM/YYYY')}</h5>
                  {corsoUtenti.map(user => {
                    const isPrenotato = newExam.utenti?.some(u => u.id_utente === user.id_utente && u.prenotato);
                    return (
                      <div key={user.id_utente} className="d-flex justify-content-between align-items-center mb-2">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`user-${user.id_utente}`}
                            checked={isPrenotato || selectedExamUsers.some(u => u.id_utente === user.id_utente)}
                            onChange={() => !isPrenotato && handleUserSelection(user)}
                            disabled={isPrenotato}
                          />
                          <label className="form-check-label" htmlFor={`user-${user.id_utente}`} style={{ opacity: 1, color: 'inherit' }}>
                            {user.nome} {user.cognome} ({user.luogo_nascita}, {moment(user.data_nascita).format('DD/MM/YYYY')})
                            {isPrenotato && <span className="badge bg-success ms-2">Prenotato</span>}
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            }     
          </>
        );
      default:
        return null;
    }
  };

  const getBackPath = () => {
    if (classe?.corso?.modalita_erogazione === TIPO_CORSO.TELEMATICA_ASINCRONA) {
      return "/gestione-corsi"
    } else {
      return "/gestione-classi"
    }
  }

  if (!classe) {
    return <div>Caricamento...</div>;
  }

  return (
    <div className={`card custom-card w-100 shadow ${isWideScreen ? "p-5" : "px-1 py-3"}`} style={{ minHeight: "700px" }}>
      <div className="container">
        <ComebackArrow path={getBackPath()} />
        <div className="d-flex justify-content-between custom-card-header mb-3">
          <div className="d-flex gap-3 custom-card-title my-2">
            <div className={`custom-icon-container`}>
              {classe?.corso?.immagine_corso ? 
                <img src={classe?.corso?.immagine_corso} className='custom-icon' alt='immagine corso'/> : 
                <i className={`bi custom-icon bi-calendar-event fs-2`}></i>}
            </div>
            <div className="align-self-center">
              <span className="fs-4 d-block">
                <i>
                  <b>
                    <h2>{classe?.corso?.denominazione} - Esami</h2>
                  </b>
                </i>
              </span>
            </div>
          </div>
        </div>
        <div style={{ height: '100vh' }}>
        <CustomCalendar
          events={exams.map(exam => {
            const corsistiPrenotati = exam.utenti?.filter(u => u.prenotato)?.length || 0;
            const corsistiAbilitati = exam.utenti?.length || 0;
            const countdownText = examCountdowns[exam.id_calendario_esame];
            const prenotato = exam.utenti?.find(u => u.prenotato === 1);
            const esitoCorsista = prenotato?.esito_esame;
            const isAlreadyTaken = esitoCorsista && esitoCorsista !== 'non_svolto';

            let title = null;

            if (classe?.corso?.modalita_erogazione === TIPO_CORSO.TELEMATICA_SINCRONA) {
              // Titolo classico per corsi sincroni
              title = (
                <div className="d-flex flex-column">
                  <strong>Esame ({corsistiPrenotati}/{corsistiAbilitati} prenotati)</strong>
                </div>
              );
            } else {
              // Titolo per corsi asincroni
              if (prenotato) {
                let subtitle = null;

                const isExamBooked = true;
                const hasNotStarted = countdownText && countdownText !== 'INIZIATO';

                if (countdownText === 'INIZIATO' && !isAlreadyTaken) {
                  subtitle = (
                    <Button
                      variant="success"
                      size="sm"
                      style={{ width: '120px' }}
                      className="mt-2 fw-bold d-inline-flex align-items-center justify-content-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        enterJoinRoom(exam.id_calendario_esame, exam);
                      }}
                    >
                      <i className="bi bi-camera-video-fill me-2 fs-5"></i>
                      Accedi
                    </Button>
                  );
                } else if (hasNotStarted) {
                  subtitle = <div className="text-muted small">{countdownText}</div>;
                }

                title = (
                  <div className="d-flex flex-column">
                    <strong>{prenotato.cognome} {prenotato.nome} {isAlreadyTaken ? ` - ${esitoCorsista}` : ''}</strong>
                    {subtitle}
                  </div>
                );
              } else {
                title = "Non prenotato";
              }
            }

            return {
              id_calendario_esame: exam.id_calendario_esame,
              title,
              start: new Date(`${exam?.data}T${exam?.ora_inizio}`),
              end: new Date(`${exam?.data}T${exam?.ora_fine}`),
              allDay: false,
              color: corsistiPrenotati > 0 ? 'green' : 'red'
            };
          })}
          availableViews={["month", "agenda"]}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          minDate={minDate}
          isLoading={loadingStateEsami.getEsamiByIdClasse}
        />

          {/* <CustomCalendar
            events={exams.map(exam => {
              //var stringaCorsistiPrenotati = "";
              const corsistiPrenotati = exam.utenti?.filter(u => u.prenotato)?.length || 0;
              //console.log(exam.utenti.cognome);
              const corsistiAbilitati = exam.utenti?.length || 0;
              const isSincronoTitle = classe?.corso?.modalita_erogazione === TIPO_CORSO.TELEMATICA_SINCRONA 
                ? `Esame (${corsistiPrenotati}/${corsistiAbilitati} prenotati)` 
                : corsistiPrenotati > 0 
                  ? `${exam.utenti?.find(u => u.prenotato === 1)?.cognome || ''} ${exam.utenti?.find(u => u.prenotato === 1)?.nome || ''}` 
                  : `Non prenotato`;
              return {
                id_calendario_esame: exam.id_calendario_esame,
                title: isSincronoTitle,
                start: new Date(`${exam?.data}T${exam?.ora_inizio}`),
                end: new Date(`${exam?.data}T${exam?.ora_fine}`),
                allDay: false,
                color: corsistiPrenotati > 0 ? 'green' : 'red'
              };
            })}
            availableViews={["month", "agenda"]}
            //views={["month", "agenda"]}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            minDate={minDate}
            isLoading={loadingStateEsami.getEsamiByIdClasse}
          /> */}
        </div>
        <CustomModal
          show={showModal}
          onHide={handleCloseModal}
          headerText={<h5 className="form-control-static">{formatDate(newExam.data)}</h5>}
          size={modalMode === MODAL_TYPES.DELETE || modalMode === MODAL_TYPES.EDIT || modalMode === MODAL_TYPES.MANAGE_USERS || modalMode === MODAL_TYPES.NEW ? "md" : "lg"}
          //btnText={"Salva modifiche"}
          btnText={
            (modalMode === MODAL_TYPES.MANAGE_USERS && classe?.corso?.modalita_erogazione === TIPO_CORSO.TELEMATICA_ASINCRONA && userPrenotato)
              ? "Annulla Prenotazione"
              : "Salva modifiche"
          }
          body={renderModalBody()}
          btnCancelText={'Chiudi'}
          closeButton={true}
          isLoading={loadingStateEsami.isLoadingEsameCreating || loadingStateEsami.isLoadingEsameUpdate || loadingStateEsamiUtenti.isLoadingEsameUtenteCreating || loadingStateEsamiUtenti.isLoadingEsameUtenteUpdate}
          onUndoChanges={handleCloseModal}
          onSave={
            (modalMode === MODAL_TYPES.MANAGE_USERS && classe?.corso?.modalita_erogazione === TIPO_CORSO.TELEMATICA_ASINCRONA && userPrenotato)
              ? () => setShowConfirmDeleteModal(true)
              : (modalMode === MODAL_TYPES.NEW || modalMode === MODAL_TYPES.EDIT)
                ? handleSaveExam
                : modalMode === MODAL_TYPES.MANAGE_USERS
                  ? handleSaveUserSelection
                  : null
          }
          // onSave={
          //   modalMode === MODAL_TYPES.NEW || modalMode === MODAL_TYPES.EDIT 
          //     ? handleSaveExam 
          //     : modalMode === MODAL_TYPES.MANAGE_USERS 
          //       ? handleSaveUserSelection 
          //       : null
          // }
          hideSaveBtn={
            modalMode === MODAL_TYPES.MANAGE_USERS &&
            classe?.corso?.modalita_erogazione === TIPO_CORSO.TELEMATICA_ASINCRONA &&
            !userPrenotato
          }
          //hideSaveBtn={modalMode === MODAL_TYPES.MANAGE_USERS && classe?.corso?.modalita_erogazione === TIPO_CORSO.TELEMATICA_ASINCRONA}
          hideButtons={modalMode !== MODAL_TYPES.NEW && modalMode !== MODAL_TYPES.EDIT && modalMode !== MODAL_TYPES.MANAGE_USERS}
        />
        <CustomModal
          show={showConfirmDeleteModal}
          onHide={() => setShowConfirmDeleteModal(false)}
          headerText={<h5 className="form-control-static">Conferma Annullamento</h5>}
          size="md"
          body={
            <p>Sei sicuro di voler annullare la prenotazione di <b>{userPrenotato?.nome} {userPrenotato?.cognome}</b>?</p>
          }
          btnText={"Conferma Annullamento"}
          btnCancelText={"Chiudi"}
          onUndoChanges={() => setShowConfirmDeleteModal(false)}
          onSave={handleConfirmCancelUserBooking}
          isLoading={loadingStateEsamiUtenti?.isLoadingEsameUtenteDelete}
        />
      </div>
    </div>
  );
}
